/**
 * Shared helpers for displaying serve periods in chronological order and
 * highlighting the "relevant" serve period (same rules as volunteers table).
 */

/** Local calendar day for "today" (midnight normalized for display logic only). */
export function getLocalTodayMidnight(): Date {
  const t = new Date()
  return new Date(t.getFullYear(), t.getMonth(), t.getDate())
}

/** Y-M-D parts from a value (YYYY-MM-DD prefix, or local calendar from Date). */
export function toLocalYmdParts(value: unknown): { y: number; m: number; d: number } | null {
  if (value == null) return null
  if (typeof value === 'string') {
    const prefix = value.trim().slice(0, 10)
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(prefix)
    if (!m) return null
    return { y: +m[1], m: +m[2], d: +m[3] }
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() }
  }
  return null
}

function ymdCmp(
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number }
): number {
  if (a.y !== b.y) return a.y - b.y
  if (a.m !== b.m) return a.m - b.m
  return a.d - b.d
}

function ymdToNum(y: number, m: number, d: number): number {
  return y * 10000 + m * 100 + d
}

/** Sort key from period start (from, else to). Missing dates sort last. */
export function servePeriodSortKey(
  from: Date | string | undefined | null,
  to: Date | string | undefined | null
): number {
  const p = toLocalYmdParts(from) ?? toLocalYmdParts(to)
  return p ? ymdToNum(p.y, p.m, p.d) : Number.POSITIVE_INFINITY
}

/** Stable chronological sort (earliest first). */
export function sortServePeriodsChronologically<
  T extends { from?: Date | string | null; to?: Date | string | null },
>(periods: T[]): T[] {
  return [...periods].sort(
    (a, b) => servePeriodSortKey(a.from, a.to) - servePeriodSortKey(b.from, b.to)
  )
}

/**
 * True if today's local calendar day falls within [from, to] inclusive.
 * Uses calendar Y-M-D only (matches volunteers-table / avoids UTC midnight bugs).
 */
export function isTodayInServePeriodLocal(
  from: Date | string | undefined | null,
  to: Date | string | undefined | null
): boolean {
  const t = getLocalTodayMidnight()
  const today = { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() }
  const f = toLocalYmdParts(from)
  const end = toLocalYmdParts(to)
  if (!f || !end) return false
  return ymdCmp(today, f) >= 0 && ymdCmp(today, end) <= 0
}

function parseServePeriodRegionId(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : parseInt(String(value), 10)
  return !isNaN(n) ? n : null
}

/**
 * True if some period has `regionId` and is upcoming or current: local end date (`to`) is on or after today.
 * Fully past periods (end before today) do not count. Requires both `from` and `to`.
 */
export function hasActiveOrFutureServePeriodForRegion(
  periods: Array<{ from?: unknown; to?: unknown; regionId?: unknown }> | undefined | null,
  regionId: number
): boolean {
  if (!periods?.length) return false
  const t = getLocalTodayMidnight()
  const today = { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() }
  for (const p of periods) {
    if (parseServePeriodRegionId(p.regionId) !== regionId) continue
    const start = toLocalYmdParts(p.from)
    const end = toLocalYmdParts(p.to)
    if (!start || !end) continue
    if (ymdCmp(end, today) >= 0) return true
  }
  return false
}

/** Field-array indices in chronological order by watched period values. */
export function sortServePeriodIndicesChronologically(
  length: number,
  getFromTo: (index: number) => { from: unknown; to: unknown }
): number[] {
  const indices = Array.from({ length }, (_, i) => i)
  return indices.sort((a, b) => {
    const ka = servePeriodSortKey(
      getFromTo(a).from as Date | string | undefined,
      getFromTo(a).to as Date | string | undefined
    )
    const kb = servePeriodSortKey(
      getFromTo(b).from as Date | string | undefined,
      getFromTo(b).to as Date | string | undefined
    )
    return ka - kb
  })
}

/**
 * Which row to highlight — same priority as volunteers-table `findRelevantServePeriod`:
 * 1. Period that contains today (inclusive calendar dates)
 * 2. Else next upcoming period (earliest start strictly after today)
 * 3. Else most recent past (last period in chronological order)
 */
export function findRelevantServePeriodFieldIndexForHighlight(
  sortedFieldIndices: number[],
  getFromTo: (index: number) => { from: unknown; to: unknown }
): number | null {
  const t = getLocalTodayMidnight()
  const today = { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() }

  for (const idx of sortedFieldIndices) {
    const { from, to } = getFromTo(idx)
    if (isTodayInServePeriodLocal(from as Date | string | undefined, to as Date | string | undefined)) {
      return idx
    }
  }

  for (const idx of sortedFieldIndices) {
    const { from } = getFromTo(idx)
    const f = toLocalYmdParts(from)
    if (!f) continue
    if (ymdCmp(f, today) > 0) return idx
  }

  if (sortedFieldIndices.length === 0) return null
  return sortedFieldIndices[sortedFieldIndices.length - 1]
}
